import { useTranslation } from 'react-i18next';
import { GitBranch, Star, Unlink } from 'lucide-react';
import {
  cn,
  Avatar,
  AvatarFallback,
  Badge,
  Dialog,
  DialogShell,
  DialogShellBody,
  DialogShellHeader
} from '@bliks-io/ui';
import type { ImpactedProcess } from '@bliks-io/contracts';

interface StepExplorerModalProps {
  process: ImpactedProcess | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canViewSensitiveFields?: boolean;
}

export function StepExplorerModal({
  process,
  open,
  onOpenChange,
  canViewSensitiveFields
}: StepExplorerModalProps) {
  const { t } = useTranslation();

  const renderRating = (rating: number | null) => (
    <div className="flex items-center gap-1">
      <Star
        className={`h-4 w-4 ${rating ? 'fill-[#FFC700] text-[#FFC700]' : 'text-fg-disabled'}`}
      />
      <span className={rating ? '' : 'text-muted-foreground'}>
        {rating ? rating.toFixed(1) : '-'}
      </span>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogShell size="md">
        <DialogShellHeader
          icon={<GitBranch className="h-5 w-5" />}
          title={process?.name ?? ''}
          subtitle={t('organization.systems.processesImpacted.stepExplorer')}
          onClose={() => onOpenChange(false)}
        />
        <DialogShellBody>
          {!process?.steps.length && (
            <p className="text-sm text-muted-foreground py-4 text-center">
              {t('common.noItemsFound')}
            </p>
          )}
          {process?.steps.map((step) => {
            const hasAnyBreak = step.hasMediaBreak || step.hasIntraStepMediaBreak;
            return (
              <div
                key={step._id}
                className={cn(
                  'flex items-start gap-4 py-3 pl-4',
                  hasAnyBreak
                    ? 'border-l-4 border-l-danger-solid bg-danger-surface'
                    : 'border-l-4 border-l-info-solid'
                )}
              >
                {hasAnyBreak && <Unlink className="h-4 w-4 text-danger shrink-0 mt-1" />}
                <Avatar size="sm" className="shrink-0 text-info bg-info-surface-strong">
                  <AvatarFallback className="text-info bg-info-surface-strong">
                    {step.stepNumber}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{step.name}</div>
                  {step.generatedDescription && (
                    <div className="text-sm text-muted-foreground line-clamp-2 mt-0.5">
                      {step.generatedDescription}
                    </div>
                  )}
                  {step.department && (
                    <Badge variant="outline" className="mt-1">
                      {step.department.abbreviation || step.department.name}
                    </Badge>
                  )}
                </div>
                {canViewSensitiveFields && (
                  <div className="shrink-0">{renderRating(step.rating)}</div>
                )}
              </div>
            );
          })}
        </DialogShellBody>
      </DialogShell>
    </Dialog>
  );
}
